import { observable, computed, makeObservable, reaction, runInAction, action } from "mobx";
import { Item, File } from "src/models";
import { DataType, Feedback, IItem, ItemType, SpaceType, Services} from 'src/types';
import { AuthStore, TagStore, FileStore, LogStore, UserStore, CommentStore, MeetingStore, MilestoneStore, BaseStore, DeliverableStore, QuestionStore, SpaceStore, AgendaStore, ItemStore } from '.';
import { FeathersService, Application, Service } from '@feathersjs/feathers/lib';
import client, { socket } from 'src/feathers';
import { computedFn, keepAlive } from "mobx-utils";
import { getRoutePath, Page, RoomView } from "src/routes";

// type AppStatus = "uninitialized" | "initialized";
// type BackendStatus = "disconnected" | "connected";

export type ItemStores = Record<ItemType, ItemStore<Item<IItem>, IItem>>;

export class RootStore {
  userStore      = new UserStore(this);
  routingStore   = this.authStore.routingStore;
  spaceStore     = new SpaceStore(this);
  questionStore  = new QuestionStore(this);
  delivStore     = new DeliverableStore(this);
  milestoneStore = new MilestoneStore(this);
  tagStore       = new TagStore(this);
  agendaStore    = new AgendaStore(this);
  commentStore   = new CommentStore(this);
  fileStore      = new FileStore(this);
  logStore       = new LogStore(this);
  meetingStore   = new MeetingStore(this);
  
  itemStores: Record<DataType.Question | DataType.Deliverable | DataType.Milestone | DataType.Meeting, ItemStore<any, any>> = {
    [DataType.Question]:    this.questionStore,
    [DataType.Deliverable]: this.delivStore,
    [DataType.Milestone]:   this.milestoneStore,
    [DataType.Meeting]:     this.meetingStore,
  }
  showFeedbackModal:        boolean = false;
  private feedback_service: FeathersService<Application<any>, Service<any>> = client.service('feedback');
  private stores:           BaseStore[] = [];

  constructor(public authStore: AuthStore) {

    makeObservable(this, {
      getActiveItem:     computed,
      getActiveItemType: computed,
      showFeedbackModal: observable,
      getItemById:       action,
    });

    this.stores = Object.keys(this).filter(k => k.endsWith("Store") && k !== "rootStore").map(k => (this as unknown as { [key: string]: BaseStore })[k]);

    reaction(() => this.authStore.isLoggedIn, () => {
      if (this.authStore.isLoggedIn) this.init()
      if (!this.authStore.isLoggedIn) this.stores.forEach(s => s.clear());
    });
  }

  get getActiveItem(): Item<IItem> {
    return this.questionStore?.getActive || 
      this.delivStore?.getActive || 
      this.milestoneStore?.getActive || 
      this.meetingStore?.getActive;
  }

  get getActiveItemType(): DataType {
    return this.getActiveItem?.data_type;
  }

  getItemById = (item_id: number): Item<IItem> => {
    return this.questionStore?.container[item_id] || 
    this.delivStore?.container[item_id] || 
    this.milestoneStore?.container[item_id] || 
    this.meetingStore?.container[item_id];
  }

  init() {
    this.stores.forEach(s => s.init());
  }

  toggleFeedbackModal = (toggle?: boolean) => {
    this.showFeedbackModal = toggle ? toggle : !this.showFeedbackModal;
  };

  submitFeedback = async (feedback: Feedback): Promise<boolean> => {
    try {
      const { type, message } = feedback;
      await Promise.all(feedback.files.map(async (f) => {
        return await f.upload(false);
      }));
      const attachmentLinks = feedback.files.map((u: File) => {
        return {
          href: u.key,
          filename: u.title,
          contentType: u.mime
        }
      });
      const attachments = attachmentLinks;
      const workspace_id = this.authStore.getCurWorkspaceID;
      await this.feedback_service.create({
        type, message, attachments, workspace_id
      });
      return true
    } catch (error) {
      console.error('Error send feedback', error);
      return false;
    }
  }
}
